import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin } from 'antd';

import PackagesHistory from 'leash/components/packagesHistory/PackagesHistory';
import { DetailsCards, DetailsLoadingWrapper } from 'leash/pages/details/Details.styles';
import { ProposalForm } from 'leash/pages/details/proposalForm/ProposalForm';
import { useDetailsState } from 'leash/pages/details/useDetailsState';
import { getSelectedEligibility } from 'leash/utilities/travelEligibility/travelEligibility';
import { LeashRoutes } from 'leash/utilities/const';
import { useLayoutState } from 'shared/components/layout/useLayoutState';
import { CardTabs } from 'shared/components/cards/CardsTabs';
import { apiPackage } from 'utilities/http/api/leash/apiPackage';
import { notify } from 'utilities/notifications/notify';
import { BreadcrumbItem, TravelSequence } from 'types';

import { Sequences } from '../sequences/Sequences';

interface RouteParameters {
  orderId: string;
}

export function DetailsContent() {
  const [isFetchingPackage, setIsFetchingPackage] = useState(false);
  const [disableFinalizeBySequences, setDisableFinalizeBySequences] = useState(false);
  const { orderId }: RouteParameters = useParams();
  const {
    state: { travelPlan, sequences },
    dispatch,
  } = useDetailsState();
  const { dispatch: dispatchLayout } = useLayoutState();
  const leadId = travelPlan?.leadId;

  const processChangesAndValidations = (modifiedSequences: TravelSequence[]): void => {
    if (
      !sequences ||
      (sequences && sequences.length === 0) ||
      modifiedSequences.length === 0
    ) {
      setDisableFinalizeBySequences(true);
    } else {
      const newSequences = modifiedSequences.some((sequence) => sequence.isNew === true);
      setDisableFinalizeBySequences(sequences.length === 0 || newSequences);
    }
  };

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        key: 'main',
        label: 'Main',
        path: '/',
      },
      {
        key: 'leash',
        label: 'Leash',
      },
      {
        key: 'allPackages',
        label: 'All Packages',
        path: LeashRoutes.Packages,
      },
      {
        key: 'travelMethod',
        label: `${leadId ?? ''}`,
      },
    ];
    dispatchLayout({ type: 'SET_BREADCRUMBS', breadcrumbs });
  }, [leadId]);

  const fetchPackage = async () => {
    try {
      setIsFetchingPackage(true);
      const response = await apiPackage.getById(orderId);

      if (!response.isOk) {
        throw Error(`Couldn't fetch package with id: ${orderId}`);
      }

      const {
        travelPlan: { address, puppy, travelPackage },
      } = response.json;

      await apiPackage.getTravelEligibility(puppy.id, address).then((resp) => {
        if (!resp.isOk) {
          throw Error('Error fetching travel eligibility');
        }

        const travelEligibility = resp.json.eligibility;

        dispatch({
          type: 'SET_TRAVEL_ELIGIBILITY',
          travelEligibility,
        });

        dispatch({
          type: 'SET_TRAVEL_ELIGIBILITY_SELECTED',
          travelEligibilitySelected: getSelectedEligibility(
            travelPackage.travelTypeId,
            travelPackage.travelMethodId,
            travelEligibility,
          ),
        });
      });

      dispatch({ type: 'SET_TRAVEL_PLAN', travelPlan: response.json.travelPlan });
    } catch (e) {
      notify.error(e.message);
    }
  };

  useEffect(() => {
    fetchPackage();
  }, []);

  useEffect(() => {
    setIsFetchingPackage(false);
    processChangesAndValidations(sequences as TravelSequence[]);
  }, [travelPlan, sequences]);

  const renderSpinner = () => {
    return (
      <DetailsLoadingWrapper>
        <Spin size="large" />
      </DetailsLoadingWrapper>
    );
  };

  if (!travelPlan) {
    return renderSpinner();
  }

  return (
    <>
      <ProposalForm disabledFinalizeBySequences={disableFinalizeBySequences} />
      {isFetchingPackage ? (
        renderSpinner()
      ) : (
        <Sequences
          fetchPackage={fetchPackage}
          onSequencesChanges={processChangesAndValidations}
        />
      )}
      <DetailsCards>
        <CardTabs
          puppy={{ puppy: travelPlan.puppy, soldDate: travelPlan.purchasedAt }}
          customer={{
            titlePrefix: 'Contact',
            person: travelPlan.customer,
            deliveryAddress: travelPlan.address,
            leadId,
          }}
          breeder={{
            titlePrefix: 'Breeder',
            person: travelPlan.breeder,
          }}
        />
      </DetailsCards>
      <PackagesHistory travelPackageId={travelPlan.travelPackage.id} />
    </>
  );
}
